import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SpriteIcon } from '@/components/SpriteIcon';

interface RecoveryCodeListProps {
  codes: string[];
  /** Names the downloaded file; falls back to a generic name. */
  accountLabel?: string;
}

/**
 * The one and only display of a freshly generated set of recovery codes.
 *
 * The server stores hashes, so these values exist nowhere else the moment this
 * component unmounts — hence copy and download alongside the list, rather than
 * leaving the user to select eleven-character strings by hand.
 *
 * Shared by the enrolment screen and the profile so the two cannot drift into
 * showing the same secret with different warnings around it.
 */
export function RecoveryCodeList({ codes, accountLabel }: RecoveryCodeListProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const asText = codes.join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is refused on insecure origins and by some policies.
      // The codes are on screen and downloadable, so this is not worth an error.
      setCopied(false);
    }
  };

  const download = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const header = [
      '# Stellantis Dashboard - two-step verification recovery codes',
      `# Account: ${accountLabel ?? 'unknown'}`,
      `# Generated: ${stamp}`,
      '#',
      '# Each code works once. Keep this file somewhere only you can reach.',
      '',
    ].join('\n');

    const blob = new Blob([header + asText + '\n'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `dashboard-recovery-codes-${stamp}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="alert alert-warning d-flex align-items-start" role="alert">
        <SpriteIcon name="alert-triangle" size={18} className="sa-icon me-2 flex-shrink-0" />
        <div>
          <strong>{t('auth:Auth.MFA.SaveCodesTitle', 'Save these codes now')}</strong>
          <p className="mb-0 mt-2 small">
            {t(
              'auth:Auth.MFA.SaveCodesMessage',
              'This is the only time they will be shown. Each code signs you in once if you lose your authenticator. Store them somewhere separate from your phone.',
            )}
          </p>
        </div>
      </div>

      <ul className="list-unstyled row row-cols-2 g-2 mb-3 font-monospace text-center">
        {codes.map((code) => (
          <li key={code}>
            <code className="d-block p-2 bg-body-tertiary rounded user-select-all">{code}</code>
          </li>
        ))}
      </ul>

      <div className="d-flex gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm flex-fill d-inline-flex align-items-center justify-content-center"
          onClick={() => void copy()}
        >
          <SpriteIcon
            name={copied ? 'check' : 'copy'}
            size={14}
            className="sa-icon me-1 flex-shrink-0"
          />
          {copied ? t('Copied', 'Copied') : t('Copy', 'Copy')}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm flex-fill d-inline-flex align-items-center justify-content-center"
          onClick={download}
        >
          <SpriteIcon name="download" size={14} className="sa-icon me-1 flex-shrink-0" />
          {t('Download', 'Download')}
        </button>
      </div>
    </>
  );
}
